import { AsyncAutocompleteSource } from '@components/v2/form/AsyncAutocomplete/asyncAutocomplete.types';
import { SelectOption, SelectOptionValue } from '@components/v2/form/Select/select.types';
import { useUpdateCatalogNavigationPreferences } from '../../../../../../../../../services/modules/catalogs/catalogs.mutations';
import { PATH as FILTER_OPTIONS_PATH } from '../../../../../../../../../services/modules/filterOptions/repositories/onlineFilterOptions.repository';
import useIsAdminSeller from '../../../../../../../../../stores/ShowUnavailableProducts/hooks/useIsAdminSeller';
import { getAvailabilityLangEnum } from '../../../../../../../../products/ProductAvailabilityRenderer';
import { AvailabilityEnum } from '../../../../../../../../products/ProductTypes';
import { isPreOrder } from '../../../../../../../../products/utils/availability.utils';
import { useTranslation } from '../../../../../../../../translation/TranslationContext';
import { CATALOG_CREATION_NAVIGATION_AVAILABILITIES } from '../../../../../../../creation/catalogCreationNavigation.consts';
import { UpdateCatalogNavigationPreferencesParams } from '../../../../../../../repositories/types/catalogs.repository.types';
import { getDeliveryWindowLabels } from '../../../catalogDetailsPanel.utils';
import { CatalogNavigationSectionProps } from '../CatalogNavigationSection';

const DELIVERY_WINDOWS_QUERY_LIMIT = 20;

const useCatalogNavigationSection = ({
  id,
  isReadOnly,
  navigationAvailabilities,
  navigationDeliveryWindows,
  showUnavailableProductsForSale,
}: CatalogNavigationSectionProps) => {
  const { translate } = useTranslation();
  const isAdminSeller = useIsAdminSeller();
  const { mutate } = useUpdateCatalogNavigationPreferences({ catalogId: id });

  const availabilityLabels = getAvailabilityLangEnum(false);
  const availabilityOptions: SelectOption[] = CATALOG_CREATION_NAVIGATION_AVAILABILITIES.map(
    (availability) => ({
      value: availability,
      label: translate(availabilityLabels[availability]),
    }),
  );

  const availabilities = (navigationAvailabilities ?? []).filter((availability) =>
    CATALOG_CREATION_NAVIGATION_AVAILABILITIES.includes(availability),
  );
  const hasPreOrderAvailability = availabilities.some(isPreOrder);

  const deliveryWindows = navigationDeliveryWindows ?? [];
  const deliveryWindowsLabel = getDeliveryWindowLabels(deliveryWindows)
    .map((deliveryWindow) => deliveryWindow.label)
    .join(', ');

  const deliveryWindowsSource: AsyncAutocompleteSource = {
    url: `${FILTER_OPTIONS_PATH}/delivery-windows`,
    queryField: 'name',
    limit: DELIVERY_WINDOWS_QUERY_LIMIT,
    order: { name: 'ASC' },
    defaultQuery: {
      basedOnList: 'catalogProducts',
      catalogId: id,
      onlyActiveProducts: true,
      isAdminSeller,
    },
  };

  const isNavigationLocked = Boolean(showUnavailableProductsForSale);
  const isFieldDisabled = isReadOnly || isNavigationLocked;

  const submitAvailabilities = (values: SelectOptionValue[]) => {
    const nextAvailabilities = values as AvailabilityEnum[];
    const payload: UpdateCatalogNavigationPreferencesParams = {
      navigationAvailabilities: nextAvailabilities,
    };

    if (!nextAvailabilities.some(isPreOrder)) {
      payload.navigationDeliveryWindowIds = [];
    }

    mutate(payload);
  };

  const submitDeliveryWindows = (deliveryWindowIds: string[]) =>
    mutate({ navigationDeliveryWindowIds: deliveryWindowIds });

  return {
    isFieldDisabled,
    isNavigationLocked,
    availabilities,
    availabilityOptions,
    deliveryWindows,
    deliveryWindowsLabel,
    deliveryWindowsSource,
    isDeliveryWindowsDisabled: isFieldDisabled || !hasPreOrderAvailability,
    submitAvailabilities,
    submitDeliveryWindows,
  };
};

export default useCatalogNavigationSection;
